import axios from "axios";
import React, { useState } from "react";
import Link from "next/link";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/20/solid";
import { useRouter } from "next/navigation";
import Toastify from "toastify-js";
import "toastify-js/src/toastify.css";

const API = "https://backend.kobiworks.com";

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const showToast = (message, type = "info") => {
  Toastify({
    text: message,
    duration: 4000,
    style: {
      background: type === "error" ? "#ef4444" : "#111827",
      borderRadius: "5px",
    },
    className: "shadow-xl border-none rounded-lg",
    position: "right",
    stopOnFocus: false,
  }).showToast();
};

/**
 * Signup — three fields, one step.
 *
 * This used to be a three-step wizard asking for ten things, including a
 * REQUIRED company logo upload. A logo upload is not a field, it is an errand:
 * someone signing up from a phone had to have an image file to hand before they
 * could have an account at all. Company address, phone, website and logo are now
 * collected in Settings, once there is a reason to care.
 *
 * The password is asked for ONCE. The confirm field was the second-highest
 * drop-off input on the form and a show/hide toggle solves the same problem.
 * The server enforces the length rule either way, so nothing rests on this file
 * being honest.
 *
 * On success the API returns a token — it always did; the old page threw it away
 * and sent the user to /login to type their password a second time. Now it logs
 * them straight in.
 */
function Signup() {
  const router = useRouter();

  const [data, setData] = useState({
    companyName: "",
    email: "",
    password1: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => {
    setData({ ...data, [key]: e.target.value });
    if (errors[key]) setErrors({ ...errors, [key]: undefined });
  };

  const validate = () => {
    const next = {};
    if (!data.companyName.trim())
      next.companyName = "What is your business called?";
    if (!data.email.trim())
      next.email = "We need an email to create your account";
    else if (!validateEmail(data.email))
      next.email = "That does not look like an email address";
    if (!data.password1) next.password1 = "Choose a password";
    else if (data.password1.length < 8)
      next.password1 = "At least 8 characters";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/company/onboarding`, {
        companyName: data.companyName.trim(),
        email: data.email.trim(),
        password1: data.password1,
      });

      // Straight in. The API hands back a session; sending the user to /login to
      // retype the password they just chose is a step that only loses people.
      if (res?.data?.token) {
        localStorage.setItem("token", res.data.token);
        localStorage.removeItem("signUpData");
        showToast("Welcome aboard.");
        router.push("/dashboard?welcome=1");
        return;
      }

      // No token but a 2xx — do not pretend we have a session we do not have.
      showToast("Account created. Please sign in.");
      router.push("/login");
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message;
      if (status === 409) {
        setErrors({ email: message || "That email already has an account." });
      }
      showToast(message || "Something went wrong. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const field =
    "block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6";

  return (
    <div className="flex min-h-screen bg-white">
      <div className="flex w-full flex-col justify-center px-6 py-12 lg:w-1/2 lg:px-20">
        <div className="mx-auto w-full max-w-sm">
          <img src="/logo.png" alt="" className="h-10 w-auto" />
          <h1 className="mt-8 text-2xl font-bold tracking-tight text-gray-900">
            Get started free
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Create your account. No credit card required.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" noValidate>
            <div>
              <label
                htmlFor="companyName"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Business name
              </label>
              <input
                id="companyName"
                name="organization"
                type="text"
                autoComplete="organization"
                autoFocus
                value={data.companyName}
                onChange={set("companyName")}
                placeholder="Azupin Glass"
                className={`${field} ${errors.companyName ? "ring-red-500" : "ring-gray-300"}`}
              />
              {errors.companyName ? (
                <p className="mt-1 text-xs text-red-600">
                  {errors.companyName}
                </p>
              ) : null}
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Work email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={data.email}
                onChange={set("email")}
                placeholder="you@yourbusiness.com"
                className={`${field} ${errors.email ? "ring-red-500" : "ring-gray-300"}`}
              />
              {errors.email ? (
                <p className="mt-1 text-xs text-red-600">{errors.email}</p>
              ) : null}
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={data.password1}
                  onChange={set("password1")}
                  placeholder="At least 8 characters"
                  className={`${field} pr-10 ${errors.password1 ? "ring-red-500" : "ring-gray-300"}`}
                />
                {/* Replaces the confirm-password field: the same reassurance,
                    without a second input to abandon on. */}
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password1 ? (
                <p className="mt-1 text-xs text-red-600">{errors.password1}</p>
              ) : null}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-md bg-indigo-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
            >
              {loading ? "Creating your account…" : "Get started free"}
            </button>

            <p className="text-center text-xs text-gray-500">
              No credit card required · Free to start · Cancel anytime
            </p>
          </form>

          <p className="mt-8 text-sm text-gray-600">
            Already have an account?{" "}
            <Link
              href="/login"
              className="font-semibold text-indigo-600 hover:text-indigo-500"
            >
              Sign in
            </Link>
          </p>

          {/* Positively framed on purpose. The equivalent negative line — "we
              will never spam you" — tested WORSE than saying nothing, because it
              introduces the idea of spam at the moment of handing over an
              address. */}
          <p className="mt-6 text-xs text-gray-400">
            We guarantee 100% privacy. Your information will not be shared.
          </p>
        </div>
      </div>

      {/* Trust panel. Beside the form rather than in a footer — the point is to
          be visible while someone decides whether to type their email. */}
      <div className="hidden lg:flex lg:w-1/2 lg:flex-col lg:justify-center bg-gray-50 px-20 border-l border-gray-200">
        <div className="max-w-md">
          <h2 className="text-lg font-semibold text-gray-900">
            Invoicing, inventory and reporting in one place
          </h2>
          <ul className="mt-6 space-y-4">
            {[
              "Send an invoice in under a minute",
              "Track stock across warehouses — and services that carry none",
              "See exactly who owes you money, and for how long",
              "Run several businesses from one account",
            ].map((line) => (
              <li key={line} className="flex gap-3">
                <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-indigo-600" />
                <span className="text-sm text-gray-700">{line}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-xs text-gray-500">
            Your data stays yours. Set up in minutes — add your logo, address
            and tax details later, from Settings.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Signup;
